import { useState } from "react";
import { useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { MapPin, Mail, Clock } from "lucide-react";
import api from "@/lib/api";
import SEO from "@/components/SEO";
import { FadeUp, Eyebrow, LineReveal } from "@/components/Motion";

const inputCls =
  "w-full rounded-xl border border-input bg-background px-4 py-3 text-sm outline-none transition-colors focus:border-orange focus:ring-2 focus:ring-orange/20";

const PROJECT_TYPES = [
  {
    value: "Film / Script Submission",
    label: "I have a film / script",
  },
  {
    value: "Service Production",
    label: "I need service production",
  },
  {
    value: "Commercial / Branded Production",
    label: "Commercial / branded production",
  },
  {
    value: "Partnership / Collaboration",
    label: "Partnership / collaboration",
  },
  {
    value: "General Inquiry",
    label: "General inquiry",
  },
];

const PROJECT_GUIDANCE = {
  "Film / Script Submission": {
    heading: "Bring us your story.",
    body:
      "Tell us what the project is, where it is in development, and what kind of producing support or collaboration you are looking for.",
    placeholder:
      "Tell us about the story, format, current stage of development, and what you're looking for from Incolour.",
  },
  "Service Production": {
    heading: "Bring your production to Ontario.",
    body:
      "Share the scope, dates and where you need local support.",
    placeholder:
      "Tell us where the production is coming from, your planned dates, locations and the local support you need.",
  },
  "Commercial / Branded Production": {
    heading: "Tell us what you're making.",
    body:
      "Share the brief, schedule, deliverables, production scope, and where you need Incolour to come in.",
    placeholder:
      "Tell us about the brief, deliverables, timeline, references, and production support you need.",
  },
  "Partnership / Collaboration": {
    heading: "Let's build something together.",
    body:
      "Tell us who you are, what you have in mind, and why you think there may be a fit with Incolour.",
    placeholder:
      "Tell us about the collaboration, your role, and what you'd like to explore together.",
  },
  "General Inquiry": {
    heading: "Start a conversation.",
    body:
      "Tell us what you have in mind and we'll make sure it reaches the right person.",
    placeholder:
      "Tell us what you'd like to discuss.",
  },
};

export default function Contact() {
  const [searchParams] = useSearchParams();
  const initialProjectType =
    searchParams.get("type") === "service" ? "Service Production" : "";

  const [form, setForm] = useState({
    name: "",
    email: "",
    company: "",
    project_type: initialProjectType,
    budget: "",
    production_origin: "",
    shoot_dates: "",
    message: "",
  });

  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);

  const set = (k) => (e) =>
    setForm((f) => ({
      ...f,
      [k]: e.target.value,
    }));

  const guidance =
    PROJECT_GUIDANCE[form.project_type] || {
      heading: "Tell us what you're dreaming up.",
      body:
        "Whether you have a script, a production coming to Ontario, or an idea for collaboration, tell us where you are and what you need.",
      placeholder:
        "Timeline, references, where you are in the process — anything helps.",
    };

  const submit = async (e) => {
    e.preventDefault();
    setSending(true);

    try {
      const { data } = await api.post("/contact", form);

      if (window.gtag) {
        window.gtag("event", "generate_lead", {
          event_category: "contact",
          event_label: form.project_type || "general inquiry",
          inquiry_type: form.project_type || "General Inquiry",
          source: "contact_form",
        });
      }

      toast.success(data.message || "Inquiry sent — talk soon!");
      setSent(true);

      setForm({
        name: "",
        email: "",
        company: "",
        project_type: "",
        budget: "",
        production_origin: "",
        shoot_dates: "",
        message: "",
      });
    } catch (err) {
      toast.error("Something went wrong — please email us directly.");
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="pt-16" data-testid="contact-page">
      <SEO
        title="Contact Our Toronto Production Team"
        description="Contact Incolour Films to bring us a film or script, plan service production in Toronto and Ontario, or explore a commercial, branded, or creative collaboration."
        path="/contact"
      />

      <section className="mx-auto max-w-7xl px-4 pb-12 pt-16 sm:px-6 lg:px-8 lg:pt-24">
        <Eyebrow>Contact</Eyebrow>

        <h1
          className="mt-5 font-display text-4xl font-extrabold leading-[1.0] tracking-tight sm:text-6xl"
          data-testid="contact-title"
        >
          <LineReveal lines={["Bring us the story.", "We'll start there."]} />
        </h1>

        <FadeUp delay={0.2}>
          <p className="mt-7 max-w-3xl text-base leading-relaxed text-muted-foreground sm:text-lg">
            Have a script you want to move forward, a production coming to
            Ontario, or an idea you want to build with us? Tell us what you're
            working on and where you need help.
          </p>
        </FadeUp>
      </section>

      <section className="mx-auto grid max-w-7xl gap-12 px-4 pb-24 sm:px-6 lg:grid-cols-12 lg:px-8">
        {/* INFO */}
        <FadeUp className="lg:col-span-5">
          <div className="overflow-hidden rounded-2xl border border-border">
            <img
              src="https://images.unsplash.com/photo-1517935706615-2717063c2225?auto=format&fit=crop&w=1600&q=80"
              alt="Toronto skyline"
              loading="lazy"
              className="aspect-[16/9] w-full object-cover"
            />
          </div>

          <div className="mt-8 space-y-6">
            <div className="flex gap-4">
              <MapPin
                size={20}
                className="mt-1 shrink-0 text-orange"
              />

              <div>
                <p className="text-xs font-mono uppercase tracking-[0.2em] text-muted-foreground">
                  Based in
                </p>

                <p className="mt-1 text-sm leading-relaxed">
                  Toronto &amp; Mississauga
                  <br />
                  Ontario, Canada
                </p>
              </div>
            </div>

            <div className="flex gap-4">
              <Mail
                size={20}
                className="mt-1 shrink-0 text-orange"
              />

              <div>
                <p className="text-xs font-mono uppercase tracking-[0.2em] text-muted-foreground">
                  Email
                </p>

                <a
                  href="mailto:connect@incolourfilms.ca"
                  onClick={() => {
                    if (window.gtag) {
                      window.gtag("event", "email_click", {
                        source: "contact_page",
                      });
                    }
                  }}
                  className="mt-1 block text-sm font-semibold hover:text-orange"
                  data-testid="contact-email-link"
                >
                  connect@incolourfilms.ca
                </a>
              </div>
            </div>

            <div className="flex gap-4">
              <Clock
                size={20}
                className="mt-1 shrink-0 text-orange"
              />

              <div>
                <p className="text-xs font-mono uppercase tracking-[0.2em] text-muted-foreground">
                  Response
                </p>

                <p className="mt-1 text-sm">
                  We aim to reply within two business days.
                </p>
              </div>
            </div>
          </div>
        </FadeUp>

        {/* FORM */}
        <FadeUp
          delay={0.12}
          className="lg:col-span-7"
        >
          <form
            onSubmit={submit}
            className="rounded-2xl border border-border bg-card p-6 sm:p-10"
            data-testid="contact-form"
          >
            {sent && (
              <div
                className="mb-6 rounded-xl border border-orange/40 bg-orange/10 px-5 py-4 text-sm"
                data-testid="contact-success-message"
              >
                Thanks — your inquiry is in. We reply within two business days.
              </div>
            )}

            <div className="mb-8 border-b border-border pb-7">
              <p className="text-xs font-mono uppercase tracking-[0.2em] text-orange">
                {form.project_type ? "Your inquiry" : "Start here"}
              </p>

              <h2 className="mt-3 font-display text-2xl font-bold tracking-tight sm:text-3xl">
                {guidance.heading}
              </h2>

              <p className="mt-3 max-w-2xl text-sm leading-relaxed text-muted-foreground sm:text-base">
                {guidance.body}
              </p>
            </div>

            <div className="grid gap-5 sm:grid-cols-2">
              <div>
                <label
                  htmlFor="cf-name"
                  className="mb-2 block text-xs font-mono uppercase tracking-[0.18em] text-muted-foreground"
                >
                  Name *
                </label>

                <input
                  id="cf-name"
                  required
                  value={form.name}
                  onChange={set("name")}
                  className={inputCls}
                  data-testid="contact-name-input"
                  placeholder="Your name"
                />
              </div>

              <div>
                <label
                  htmlFor="cf-email"
                  className="mb-2 block text-xs font-mono uppercase tracking-[0.18em] text-muted-foreground"
                >
                  Email *
                </label>

                <input
                  id="cf-email"
                  required
                  type="email"
                  value={form.email}
                  onChange={set("email")}
                  className={inputCls}
                  data-testid="contact-email-input"
                  placeholder="you@company.com"
                />
              </div>

              <div>
                <label
                  htmlFor="cf-company"
                  className="mb-2 block text-xs font-mono uppercase tracking-[0.18em] text-muted-foreground"
                >
                  Company / Organization
                </label>

                <input
                  id="cf-company"
                  value={form.company}
                  onChange={set("company")}
                  className={inputCls}
                  data-testid="contact-company-input"
                  placeholder="Optional"
                />
              </div>

              <div>
                <label
                  htmlFor="cf-type"
                  className="mb-2 block text-xs font-mono uppercase tracking-[0.18em] text-muted-foreground"
                >
                  How can we help? *
                </label>

                <select
                  id="cf-type"
                  required
                  value={form.project_type}
                  onChange={set("project_type")}
                  className={inputCls}
                  data-testid="contact-type-select"
                >
                  <option value="">Select…</option>

                  {PROJECT_TYPES.map((type) => (
                    <option
                      key={type.value}
                      value={type.value}
                    >
                      {type.label}
                    </option>
                  ))}
                </select>
              </div>

              <div className="sm:col-span-2">
                <label
                  htmlFor="cf-budget"
                  className="mb-2 block text-xs font-mono uppercase tracking-[0.18em] text-muted-foreground"
                >
                  Budget range
                </label>

                <select
                  id="cf-budget"
                  value={form.budget}
                  onChange={set("budget")}
                  className={inputCls}
                  data-testid="contact-budget-select"
                >
                  <option value="">Select…</option>
                  <option>Under $25k</option>
                  <option>$25k – $100k</option>
                  <option>$100k – $500k</option>
                  <option>$500k – $2M</option>
                  <option>$2M+</option>
                  <option>Not determined yet</option>
                </select>
              </div>

              {form.project_type === "Service Production" && (
                <>
                  <div>
                    <label
                      htmlFor="cf-origin"
                      className="mb-2 block text-xs font-mono uppercase tracking-[0.18em] text-muted-foreground"
                    >
                      Where is the production coming from?
                    </label>

                    <input
                      id="cf-origin"
                      value={form.production_origin}
                      onChange={set("production_origin")}
                      className={inputCls}
                      data-testid="contact-production-origin-input"
                      placeholder="London, Mumbai, Los Angeles, Dubai..."
                    />
                  </div>

                  <div>
                    <label
                      htmlFor="cf-shoot-dates"
                      className="mb-2 block text-xs font-mono uppercase tracking-[0.18em] text-muted-foreground"
                    >
                      Planned shoot dates
                    </label>

                    <input
                      id="cf-shoot-dates"
                      value={form.shoot_dates}
                      onChange={set("shoot_dates")}
                      className={inputCls}
                      data-testid="contact-shoot-dates-input"
                      placeholder="March 2027 or March 12 to 24, 2027"
                    />
                  </div>
                </>
              )}

              <div className="sm:col-span-2">
                <label
                  htmlFor="cf-message"
                  className="mb-2 block text-xs font-mono uppercase tracking-[0.18em] text-muted-foreground"
                >
                  About the project *
                </label>

                <textarea
                  id="cf-message"
                  required
                  rows={6}
                  value={form.message}
                  onChange={set("message")}
                  className={inputCls}
                  data-testid="contact-message-input"
                  placeholder={guidance.placeholder}
                />
              </div>
            </div>

            <button
              type="submit"
              disabled={sending}
              data-testid="contact-form-submit-btn"
              className="mt-8 w-full rounded-full bg-orange px-8 py-4 text-sm font-semibold text-cream transition-transform hover:scale-[1.02] disabled:opacity-60 sm:w-auto"
            >
              {sending ? "Sending…" : "Send inquiry"}
            </button>
          </form>
        </FadeUp>
      </section>
    </div>
  );
}
